import { useEffect, useRef, useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MessageCircle, Send, Copy, Link2 } from "lucide-react";

// Lote 176 — Convite de testemunha, dentro do compromisso: o texto já vem pronto com os dados da audiência; o advogado
// informa só a testemunha e escolhe como mandar (WhatsApp, e-mail, copiar o texto ou copiar só o link). Cada testemunha
// tem o seu link de confirmação.
async function copiar(texto) {
  try { await navigator.clipboard.writeText(texto); return true; } catch { return false; }
}

export default function ConvidarTestemunha({ pub, open, onClose, onChanged, testemunha = null }) {
  const [nome, setNome] = useState("");
  const [telefone, setTelefone] = useState("");
  const [email, setEmail] = useState("");
  const [mostrarPartes, setMostrarPartes] = useState(true);
  const [texto, setTexto] = useState("");
  const [editado, setEditado] = useState(false);
  const [tid, setTid] = useState(null);
  const [busy, setBusy] = useState(false);
  const seq = useRef(0);

  // Abre limpo (ou já com os dados da testemunha, para reenviar).
  useEffect(() => {
    if (!open) return;
    setNome(testemunha?.nome || "");
    setTelefone(testemunha?.telefone || "");
    setEmail(testemunha?.email || "");
    setMostrarPartes(testemunha ? testemunha.mostrar_partes !== false : true);
    setTid(testemunha?.tid || null);
    setEditado(false);
    setTexto("");
  }, [open, testemunha]);

  const gerarTexto = async (n = nome, mp = mostrarPartes) => {
    const minha = ++seq.current;
    try {
      const r = await http.post(`/publications/${pub.pub_id}/testemunhas/previa`, { nome: n, mostrar_partes: mp });
      if (minha === seq.current) { setTexto(r.data.texto); setEditado(false); }
    } catch { /* o texto some, mas as ações avisam o erro */ }
  };

  // Enquanto o advogado não mexe no texto, ele acompanha o nome e a opção das partes.
  useEffect(() => {
    if (!open || editado) return undefined;
    const t = setTimeout(() => gerarTexto(), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, nome, mostrarPartes, pub?.pub_id]);

  const enviar = async (canal) => {
    if (nome.trim().length < 2) { toast.error("Informe o nome da testemunha"); return; }
    setBusy(true);
    try {
      const r = await http.post(`/publications/${pub.pub_id}/testemunhas/convidar`, {
        tid, nome, telefone: telefone || null, email: email || null, mostrar_partes: mostrarPartes, canal, mensagem: texto || null,
      });
      setTid(r.data.testemunha?.tid || tid);
      if (canal === "copia") {
        const ok = await copiar(r.data.texto);
        if (ok) toast.success("Convite copiado"); else toast.error("Não consegui copiar — selecione o texto e copie");
      } else if (canal === "whatsapp") {
        window.open(r.data.wa_url, "_blank", "noopener");
        toast.success("Convite aberto no WhatsApp");
      } else {
        toast.success(`E-mail enviado a ${email}`);
      }
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao preparar o convite");
    } finally { setBusy(false); }
  };

  const copiarLink = async () => {
    if (nome.trim().length < 2) { toast.error("Informe o nome da testemunha"); return; }
    setBusy(true);
    try {
      const r = await http.post(`/publications/${pub.pub_id}/testemunhas/convidar`, {
        tid, nome, telefone: telefone || null, email: email || null, mostrar_partes: mostrarPartes, canal: "copia", mensagem: "{{LINK}}",
      });
      setTid(r.data.testemunha?.tid || tid);
      if (r.data.link && (await copiar(r.data.link))) toast.success("Link copiado");
      else toast.error("Não consegui copiar o link");
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao gerar o link");
    } finally { setBusy(false); }
  };

  const h = pub?.hearing || {};
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="testemunha-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{tid ? "Convite de testemunha" : "Convidar testemunha"}</DialogTitle>
          <DialogDescription>
            Os dados da audiência já vão no texto. Informe a testemunha e escolha como enviar.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2">
            <label className="text-xs text-stone-500 block mb-1" htmlFor="tes-nome">Nome da testemunha</label>
            <Input id="tes-nome" data-testid="testemunha-nome" value={nome} onChange={(e) => setNome(e.target.value)} maxLength={120} autoComplete="off" />
          </div>
          <div>
            <label className="text-xs text-stone-500 block mb-1" htmlFor="tes-fone">Telefone (WhatsApp)</label>
            <Input id="tes-fone" data-testid="testemunha-telefone" value={telefone} onChange={(e) => setTelefone(e.target.value)} inputMode="tel" placeholder="(00) 00000-0000" autoComplete="off" />
          </div>
          <div>
            <label className="text-xs text-stone-500 block mb-1" htmlFor="tes-email">E-mail (opcional)</label>
            <Input id="tes-email" data-testid="testemunha-email" value={email} onChange={(e) => setEmail(e.target.value)} inputMode="email" autoComplete="off" />
          </div>
        </div>

        <label className="flex items-start gap-2 text-sm text-stone-800 cursor-pointer">
          <input type="checkbox" data-testid="testemunha-partes" className="mt-1" checked={mostrarPartes} onChange={(e) => setMostrarPartes(e.target.checked)} />
          <span>Mencionar as partes{h.party_author || h.party_defendant ? "" : " (não há partes cadastradas neste compromisso)"}</span>
        </label>

        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-xs text-stone-500" htmlFor="tes-texto">Texto do convite (você pode ajustar)</label>
            {editado && (
              <button type="button" data-testid="testemunha-refazer" onClick={() => gerarTexto()} className="text-xs text-stone-600 underline">
                Refazer o texto
              </button>
            )}
          </div>
          <Textarea id="tes-texto" data-testid="testemunha-texto" value={texto} rows={12} className="text-sm leading-relaxed"
            onChange={(e) => { setTexto(e.target.value); setEditado(true); }} />
          <p className="text-[11px] text-stone-500 mt-1">
            <span className="font-mono">{"{{LINK}}"}</span> vira o link de confirmação desta testemunha. O e-mail sai no mesmo modelo, com botão.
          </p>
        </div>

        <div className="flex flex-wrap gap-2 pt-1">
          <Button data-testid="testemunha-whatsapp" onClick={() => enviar("whatsapp")} disabled={busy} className="bg-[#25D366] hover:bg-[#20b95a] text-white gap-2">
            <MessageCircle className="w-4 h-4" strokeWidth={2} /> Abrir WhatsApp
          </Button>
          <Button data-testid="testemunha-email-enviar" onClick={() => enviar("email")} disabled={busy} className="gap-2">
            <Send className="w-4 h-4" strokeWidth={2} /> Enviar e-mail
          </Button>
          <Button data-testid="testemunha-copiar" onClick={() => enviar("copia")} disabled={busy} variant="outline" className="gap-2">
            <Copy className="w-4 h-4" strokeWidth={2} /> Copiar texto
          </Button>
          <Button data-testid="testemunha-copiar-link" onClick={copiarLink} disabled={busy} variant="ghost" className="gap-2">
            <Link2 className="w-4 h-4" strokeWidth={2} /> Copiar link
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
